import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTheme } from '@/context/ThemeContext';
import { useLanguage } from '@/context/LanguageContext';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import LanguageSelector from '@/components/LanguageSelector';
import { Layers, Sun, Moon, CheckCircle, AlertCircle, Mail, RefreshCw, Loader2, LogIn } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const VerifyEmailPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [verifying, setVerifying] = useState(false);
  const [verificationStatus, setVerificationStatus] = useState(null); // 'success', 'error', 'expired', 'invalid', 'needs_login'
  const [errorMessage, setErrorMessage] = useState('');
  const [resending, setResending] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [loginMode, setLoginMode] = useState(false);
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [loginLoading, setLoginLoading] = useState(false);
  
  const { theme, toggleTheme } = useTheme();
  const { t } = useLanguage();
  const { user, token: authToken, login } = useAuth();
  const hasAttemptedVerification = React.useRef(false);

  // Function to verify email with token
  const verifyEmailWithToken = async (verificationToken) => {
    setVerifying(true);
    try {
      const response = await axios.post(`${API_URL}/api/auth/verify-email`, { token: verificationToken });
      
      setUserEmail(response.data.user?.email || '');
      setVerificationStatus('success');
      toast.success(t('verify.success'));
      
      // Auto-login user if token provided
      if (response.data.access_token) {
        login(response.data.access_token, response.data.user);
        
        // Redirect to dashboard after 2 seconds
        setTimeout(() => {
          navigate('/dashboard');
        }, 2000);
      }
      return true;
    } catch (error) {
      const detail = error.response?.data?.detail || '';
      if (detail.includes('expired')) {
        setVerificationStatus('expired');
      } else if (detail.includes('already verified')) {
        setVerificationStatus('success');
        toast.success('Email already verified!');
        setTimeout(() => navigate('/dashboard'), 2000);
      } else {
        setVerificationStatus('invalid');
        setErrorMessage(detail || t('verify.invalidMessage'));
      }
      return false;
    } finally {
      setVerifying(false);
    }
  };

  useEffect(() => {
    // Prevent double verification in React Strict Mode
    if (hasAttemptedVerification.current) return;
    hasAttemptedVerification.current = true;
    
    if (!token) {
      setVerificationStatus('invalid');
      setErrorMessage(t('verify.tokenMissing'));
      return;
    }

    // If user is already logged in, verify immediately
    if (user && authToken) {
      verifyEmailWithToken(token);
    } else {
      // User not logged in - show login prompt
      // This handles the Emergent preview edge protection scenario
      setVerificationStatus('needs_login');
    }
  }, [token, user, authToken]);

  // Handle login then verify
  const handleLoginAndVerify = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    
    try {
      // First login
      const loginResponse = await axios.post(`${API_URL}/api/auth/login`, {
        email: loginForm.email,
        password: loginForm.password
      });
      
      if (loginResponse.data.access_token) {
        login(loginResponse.data.access_token, loginResponse.data.user);
        toast.success('Logged in successfully!');
        
        // Now verify the email
        await verifyEmailWithToken(token);
      }
    } catch (error) {
      const detail = error.response?.data?.detail || 'Login failed';
      toast.error(detail);
    } finally {
      setLoginLoading(false);
    }
  };

  const handleResendVerification = async () => {
    const emailToUse = userEmail || loginForm.email;
    if (!emailToUse) {
      toast.error(t('verify.noEmail'));
      return;
    }
    
    setResending(true);
    try {
      const response = await axios.post(`${API_URL}/api/auth/resend-verification`, { email: emailToUse });
      
      if (response.data.already_verified) {
        toast.success(t('verify.alreadyVerified'));
        navigate('/auth');
      } else if (response.data.rate_limited) {
        toast.info('Please wait a minute before requesting another email.');
      } else {
        toast.success('Verification email sent! Check your inbox.');
      }
    } catch (error) {
      toast.error(t('verify.resendError'));
    } finally {
      setResending(false);
    }
  };

  // Loading state
  if (verifying) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">{t('verify.verifying')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex">
      {/* Left side - Content */}
      <div className="flex-1 flex flex-col justify-center px-8 lg:px-16 py-12">
        <div className="w-full max-w-md mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-12">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-10 h-10 bg-foreground rounded-xl flex items-center justify-center">
                <Layers className="w-6 h-6 text-background" />
              </div>
              <span className="font-semibold text-2xl tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
                MockupPro
              </span>
            </Link>
            <div className="flex items-center gap-2">
              <LanguageSelector variant="compact" />
              <Button variant="ghost" size="icon" onClick={toggleTheme}>
                {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </Button>
            </div>
          </div>

          {/* Success State */}
          {verificationStatus === 'success' && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3 }}
              className="text-center"
            >
              <div className="w-20 h-20 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-green-600 dark:text-green-400" />
              </div>
              <h1 
                className="text-2xl font-bold mb-2"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                {t('verify.successTitle')}
              </h1>
              <p className="text-muted-foreground mb-6">
                {t('verify.successMessage')}
              </p>
              <p className="text-sm text-muted-foreground mb-6">
                {t('verify.redirecting')}
              </p>
              <Link to="/dashboard">
                <Button variant="default" className="rounded-lg">
                  {t('verify.goToDashboard')}
                </Button>
              </Link>
            </motion.div>
          )}

          {/* Needs Login State - For edge-protected preview environments */}
          {verificationStatus === 'needs_login' && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3 }}
            >
              <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
                <LogIn className="w-8 h-8 text-blue-600 dark:text-blue-400" />
              </div>
              <h1 
                className="text-2xl font-bold mb-2 text-center"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                Verify Your Email
              </h1>
              <p className="text-muted-foreground mb-6 text-center">
                Please log in to complete your email verification.
              </p>
              
              <form onSubmit={handleLoginAndVerify} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="Enter your email"
                    value={loginForm.email}
                    onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                    required
                    data-testid="verify-login-email"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">Password</Label>
                  <Input
                    id="password"
                    type="password"
                    placeholder="Enter your password"
                    value={loginForm.password}
                    onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                    required
                    data-testid="verify-login-password"
                  />
                </div>
                
                <Button 
                  type="submit" 
                  className="w-full rounded-lg"
                  disabled={loginLoading}
                  data-testid="verify-login-submit"
                >
                  {loginLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Verifying...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Log In & Verify Email
                    </>
                  )}
                </Button>
              </form>
              
              <div className="mt-6 text-center">
                <p className="text-sm text-muted-foreground">
                  Don't remember your password?{' '}
                  <Link to="/forgot-password" className="text-primary hover:underline">
                    Reset it here
                  </Link>
                </p>
              </div>
              
              <div className="mt-4 pt-4 border-t text-center">
                <p className="text-sm text-muted-foreground mb-3">
                  Didn't receive the email?
                </p>
                <div className="space-y-2">
                  <Input
                    type="email"
                    placeholder="Enter your email to resend"
                    value={loginForm.email}
                    onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                    className="text-center"
                  />
                  <Button 
                    variant="outline"
                    onClick={handleResendVerification}
                    disabled={resending || !loginForm.email}
                    className="w-full"
                    data-testid="resend-verification-btn"
                  >
                    {resending ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Mail className="w-4 h-4 mr-2" />
                        Resend Verification Email
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </motion.div>
          )}

          {/* Expired State */}
          {verificationStatus === 'expired' && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3 }}
              className="text-center"
            >
              <div className="w-20 h-20 bg-yellow-100 dark:bg-yellow-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
                <AlertCircle className="w-10 h-10 text-yellow-600 dark:text-yellow-400" />
              </div>
              <h1 
                className="text-2xl font-bold mb-2"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                {t('verify.expiredTitle')}
              </h1>
              <p className="text-muted-foreground mb-6">
                {t('verify.expiredMessage')}
              </p>
              <Button 
                onClick={handleResendVerification}
                disabled={resending}
                className="rounded-lg"
              >
                {resending ? (
                  <span className="flex items-center gap-2">
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    {t('verify.resending')}
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Mail className="w-4 h-4" />
                    {t('verify.resendLink')}
                  </span>
                )}
              </Button>
              <p className="text-sm text-muted-foreground mt-4">
                <Link to="/auth" className="text-primary hover:underline">
                  {t('verify.backToLogin')}
                </Link>
              </p>
            </motion.div>
          )}

          {/* Invalid/Error State */}
          {(verificationStatus === 'invalid' || verificationStatus === 'error') && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3 }}
              className="text-center"
            >
              <div className="w-20 h-20 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
                <AlertCircle className="w-10 h-10 text-red-600 dark:text-red-400" />
              </div>
              <h1 
                className="text-2xl font-bold mb-2"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                {t('verify.invalidTitle')}
              </h1>
              <p className="text-muted-foreground mb-6">
                {errorMessage || t('verify.invalidMessage')}
              </p>
              <div className="flex gap-3 justify-center">
                <Link to="/auth">
                  <Button variant="outline" className="rounded-lg">
                    {t('verify.backToLogin')}
                  </Button>
                </Link>
              </div>
            </motion.div>
          )}

          {/* Resent State (Preview Mode) */}
          {verificationStatus === 'resent' && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3 }}
              className="text-center"
            >
              <div className="w-20 h-20 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
                <Mail className="w-10 h-10 text-blue-600 dark:text-blue-400" />
              </div>
              <h1 
                className="text-2xl font-bold mb-2"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                {t('verify.resentTitle')}
              </h1>
              <p className="text-muted-foreground mb-6">
                {t('verify.resentMessage')}
              </p>
              <Link to="/auth">
                <Button variant="default" className="rounded-lg">
                  {t('verify.backToLogin')}
                </Button>
              </Link>
            </motion.div>
          )}
        </div>
      </div>

      {/* Right side - Visual */}
      <div className="hidden lg:flex flex-1 bg-muted/30 items-center justify-center p-12">
        <div className="max-w-md text-center">
          <div className="w-24 h-24 bg-accent rounded-3xl flex items-center justify-center mx-auto mb-8">
            <Mail className="w-12 h-12" />
          </div>
          <h2 
            className="text-2xl font-bold mb-4"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            {t('verify.rightTitle')}
          </h2>
          <p className="text-muted-foreground">
            {t('verify.rightSubtitle')}
          </p>
        </div>
      </div>
    </div>
  );
};

export default VerifyEmailPage;
